import React from "react";

function csvEscape(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildImportErrorCsv(errors) {
  const header = "excel_row,field,value,reason";
  const lines = (errors || []).map((e) =>
    [e.row || "", e.field || "", e.value || "", e.reason || ""].map(csvEscape).join(",")
  );
  return [header, ...lines].join("\n");
}

export default function RfpExcelImportErrors({
  message,
  errors,
  errorCount,
  filename,
  onDismiss,
}) {
  const list = Array.isArray(errors) ? errors : [];
  if (!list.length && !message) return null;
  const total = errorCount || list.length;
  const truncated = total > list.length;

  const handleDownload = () => {
    const csv = buildImportErrorCsv(list);
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "rfp_import_errors.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="mt-3 mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-sm dark:border-red-800 dark:bg-red-950/40"
      data-testid="rfp-excel-import-errors"
      role="alert"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-red-900 dark:text-red-100">
            Excel import rejected — nothing was saved
          </p>
          <p className="mt-1 text-red-800 dark:text-red-200">
            {message || "Fix the listed rows in Excel and import again."}{" "}
            {total ? `${total} error${total === 1 ? "" : "s"}.` : ""}
            {truncated
              ? ` Showing the first ${list.length}. Download the report for this list, then re-import after fixing.`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-lg border border-red-400 bg-white px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-100 dark:border-red-700 dark:bg-red-950 dark:text-red-100 dark:hover:bg-red-900"
            onClick={handleDownload}
            disabled={!list.length}
          >
            Download error report
          </button>
          {onDismiss && (
            <button
              type="button"
              className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-800 hover:bg-red-100 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
      {list.length > 0 && (
        <div className="max-h-64 overflow-auto rounded-lg border border-red-200 bg-white dark:border-red-900 dark:bg-slate-950">
          <table className="w-full min-w-[36rem] text-left text-xs">
            <thead className="sticky top-0 bg-red-100 text-red-950 dark:bg-red-950 dark:text-red-100">
              <tr>
                <th className="px-3 py-2 font-semibold">Excel row</th>
                <th className="px-3 py-2 font-semibold">Field</th>
                <th className="px-3 py-2 font-semibold">Value</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e, i) => (
                <tr
                  key={`${e.row}-${e.field}-${i}`}
                  className="border-t border-red-100 odd:bg-white even:bg-red-50/40 dark:border-red-900 dark:odd:bg-slate-950 dark:even:bg-red-950/20"
                >
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono">{e.row || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{e.field || "—"}</td>
                  <td className="max-w-[12rem] truncate px-3 py-1.5" title={e.value || ""}>
                    {e.value || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-slate-800 dark:text-slate-200">{e.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
